import React, { useEffect } from "react";
import { useState } from "react";
import axios from "axios";
import { base_url } from "../../config";
import { Printer, Trash2, Newspaper, Ticket, UserPlus } from "lucide-react";

interface Pass {
  id: string;
  name: string;
  affiliation: string;
  category: string;
  events: {
    reception: boolean;
    studentReception: boolean;
    banquet: boolean;
    industryDay: boolean;
  };
  otherAccess: string;
}

interface MiniBadgeProps {
  name: string;
  affiliation: string;
  category: string;
  code: string;
}

const MiniBadge: React.FC<MiniBadgeProps> = ({ name, affiliation, category, code }) => {
  return (
    <div style={{ width: "220px" }} className="shrink-0">
      <div className="relative overflow-hidden rounded-lg h-[176px] border border-slate-700/50 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
        {/* soft corner glow */}
        <div className="absolute -top-10 -right-8 h-24 w-24 rounded-full bg-blue-500/20 blur-2xl" />
        {/* layered waves */}
        <svg
          className="absolute inset-x-0 bottom-0 w-full"
          viewBox="0 0 220 90"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,40 C30,20 60,60 110,45 C160,30 190,55 220,40 L220,90 L0,90 Z"
            fill="#3b82f6"
            fillOpacity="0.10"
          />
          <path
            d="M0,55 C40,35 70,70 120,55 C170,40 195,65 220,52 L220,90 L0,90 Z"
            fill="#6366f1"
            fillOpacity="0.12"
          />
          <path
            d="M0,68 C35,55 80,80 130,68 C175,58 200,76 220,66 L220,90 L0,90 Z"
            fill="#0ea5e9"
            fillOpacity="0.10"
          />
        </svg>
        {/* content */}
        <div className="relative h-full p-4 flex flex-col">
          <div className="ml-auto font-mono text-[11px] tracking-wide text-slate-400">
            {code}
          </div>
          <div className="m-auto text-center">
            <div className="text-base font-semibold text-slate-100 leading-tight">{name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{affiliation}</div>
            <span className="inline-block text-[11px] mt-3 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-500/30">
              {category}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-transparent";

const Card: React.FC<{ title: string; subtitle?: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  icon,
  children,
}) => (
  <section className="rounded-2xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
    <header className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/40 bg-slate-900/40">
      <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-blue-500/15 text-blue-300">
        {icon}
      </span>
      <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
      {subtitle && <span className="ml-auto text-xs text-slate-400">{subtitle}</span>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

export default function PassManagement() {
  const [passes, setPasses] = useState<Pass[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    affiliation: "",
    category: "",
    events: {
      reception: false,
      studentReception: false,
      banquet: false,
      industryDay: false,
    },
    otherAccess: "",
  });

  const handleEventChange = (event: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        [event]: checked,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.affiliation || !formData.category) {
      return;
    }

    const newPass: Pass = {
      id: Date.now().toString(),
      name: formData.name,
      affiliation: formData.affiliation,
      category: formData.category,
      events: formData.events,
      otherAccess: formData.otherAccess,
    };
    try {
      const response = await axios.post(
        `${base_url}/api/admin/post_extra`,
        newPass
      );
      newPass.id = response.data.data.pass_id;
      setPasses((prev) => [...prev, newPass]);

      // Reset form
      setFormData({
        name: "",
        affiliation: "",
        category: "",
        events: {
          reception: false,
          studentReception: false,
          banquet: false,
          industryDay: false,
        },
        otherAccess: "",
      });
    } catch (error) {
      console.error("Error creating pass:", error);
      return;
    }
  };

  const handleBadgePrint = async (badge: any) => {
    try {
      const response = await axios.post(`${base_url}/api/admin/print_press_badge_mt`, {
        id: badge,
      }, {
        responseType: 'blob' // Important: tell axios to expect a binary response
      });

      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `badge_${badge.id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error printing badge:", error);
    }
  };

  const removePass = async (id: string) => {
    try {
      await axios.post(`${base_url}/api/admin/remove_extra`, { id });
      setPasses((prev) => prev.filter((pass) => pass.id !== id));
    } catch (error) {
      console.error("Error deleting pass:", error);
      return;
    }
  };

  const getEventsList = (events: Pass["events"]) => {
    const eventList = [];
    if (events.reception) eventList.push("Reception");
    if (events.studentReception) eventList.push("Student Reception");
    if (events.banquet) eventList.push("Banquet");
    if (events.industryDay) eventList.push("Industry Day");
    return eventList;
  };

  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/get_extra`)
      .then((response) => {
        setPasses(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching exhibitors :", error);
      });
  }, []);

  const eventOptions: { key: keyof Pass["events"]; label: string }[] = [
    { key: "reception", label: "Reception" },
    { key: "studentReception", label: "Student Reception" },
    { key: "banquet", label: "Banquet" },
    { key: "industryDay", label: "Industry Day" },
  ];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-5xl px-4 py-5 space-y-4">
        <header className="flex items-baseline justify-between flex-wrap gap-2">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-blue-400" />
            Press &amp; Extra Passes
          </h1>
          <span className="text-xs text-slate-400">
            Create passes with specific event access
          </span>
        </header>

        {/* Form Section */}
        <Card title="Define New Pass" icon={<UserPlus className="h-4 w-4" />}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="name" className="block text-xs font-medium text-slate-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter full name"
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="affiliation" className="block text-xs font-medium text-slate-400 mb-1">
                  Affiliation
                </label>
                <input
                  type="text"
                  id="affiliation"
                  value={formData.affiliation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, affiliation: e.target.value }))
                  }
                  placeholder="University, Company, etc."
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="category" className="block text-xs font-medium text-slate-400 mb-1">
                  Pass Category (e.g. Press, VIP...)
                </label>
                <input
                  type="text"
                  id="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, category: e.target.value }))
                  }
                  placeholder="Enter pass category"
                  required
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Event Access
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {eventOptions.map((opt) => {
                  const checked = formData.events[opt.key];
                  return (
                    <label
                      key={opt.key}
                      htmlFor={opt.key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-medium cursor-pointer transition-colors ${
                        checked
                          ? "bg-blue-500/15 text-blue-200 border-blue-500/30"
                          : "bg-slate-900/60 text-slate-300 border-slate-700/50 hover:border-slate-600/70"
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={opt.key}
                        checked={checked}
                        onChange={(e) => handleEventChange(opt.key, e.target.checked)}
                        className="h-4 w-4 accent-blue-500"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="otherAccess" className="block text-xs font-medium text-slate-400 mb-1">
                Other Access
                <span className="ml-1 text-slate-500">
                  (optional — e.g. one specific session or talk)
                </span>
              </label>
              <input
                type="text"
                id="otherAccess"
                value={formData.otherAccess}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, otherAccess: e.target.value }))
                }
                placeholder="Optionally describe other access permissions"
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Create Pass
            </button>
          </form>
        </Card>

        {/* Passes List Section */}
        <Card
          title="Defined Passes"
          subtitle={`${passes.length} total`}
          icon={<Ticket className="h-4 w-4" />}
        >
          {passes.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No passes defined yet. Create your first pass above.
            </div>
          ) : (
            <div className="space-y-3">
              {passes.map((pass) => (
                <div
                  key={pass.id}
                  className="border border-slate-700/40 bg-slate-900/40 rounded-xl p-3 flex gap-3"
                >
                  <MiniBadge
                    name={pass.name}
                    affiliation={pass.affiliation}
                    category={pass.category}
                    code={pass.id}
                  />

                  <div className="grow space-y-2 min-w-0">
                    <div className="text-xs font-medium text-slate-400">Event Access</div>
                    <div className="flex flex-wrap gap-1.5">
                      {getEventsList(pass.events).map((event, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-200 border border-blue-500/30"
                        >
                          {event}
                        </span>
                      ))}
                      {getEventsList(pass.events).length === 0 && (
                        <span className="text-xs text-slate-500">No events selected</span>
                      )}
                    </div>
                    <div className="text-xs font-medium text-slate-400 pt-1">Other Access</div>
                    <div className="text-xs text-slate-300">
                      {pass.otherAccess || "No additional access permissions"}
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to remove this pass?")) {
                          removePass(pass.id);
                        }
                      }}
                      title="Remove pass"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-rose-300 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleBadgePrint(pass.id)}
                      title="Print badge"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-blue-200 border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 transition-colors"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                    <span className="text-[10px] text-slate-500">Badge</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
